import PropTypes from 'prop-types';
import * as R from 'ramda';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useStore } from 'react-redux';
import { lookerParams } from '../../../../../shared/lookerParams';
import { errorHandler } from '../../../../../shared/utils';
import { updateLookerTabsVisited } from '../../../../../stores/actions/lookerActions';
import VistaToggleButton from '../../../../Common/VistaToggleButton';
import { getAllOutputAttributes, getModelEmbedUrl } from '../modelOutputService';
import './yearProjectionsStyle.scss';

const propTypes = {
    activeTab: PropTypes.string,
    onLoadComplete: PropTypes.func,
    tabLabel: PropTypes.string,
};

const defaultProps = {
    tabLabel: 'FiveYearProjections',
};

function YearProjectionsTab({ activeTab, onLoadComplete, tabLabel = 'FiveYearProjections' }) {
    //#region State
    const [isPending, setIsPending] = useState(true);
    const [tableUrl, setTableUrl] = useState('');
    const [chartUrl, setChartUrl] = useState('');
    const [showChart, setShowChart] = useState(false);
    const [filterTableSuffix, setFilterTableSuffix] = useState('global');
    const [filterAttribute, setFilterAttribute] = useState('1');
    const [attributeList, setAttributeList] = useState(null);

    const { modelChangeAction, lookerTabsVisited } = useStore().getState();

    const lookerTabLabel = lookerTabsVisited[tabLabel];
    const dispatch = useDispatch();

    const { modelVersionId, customerId } = R.pathOr(
        {
            modelVersionId: null,
            customerId: null,
        },
        ['modelPayload', 'modelDetails'],
        modelChangeAction,
    );
    //#endregion

    //#region Click/Action/Event Handlers
    const loadComplete = () => {
        dispatch(updateLookerTabsVisited({ [tabLabel]: true }));
        onLoadComplete();
    };

    const handleFilterChange = (event) => {
        event.preventDefault();
        const value = event.target.value;
        setFilterAttribute(R.path([value, 0, 'id'], attributeList));
        setFilterTableSuffix(value);

        dispatch(updateLookerTabsVisited({ [tabLabel]: false }));
    };

    const handleAttributeChange = (event) => {
        event.preventDefault();
        const value = event.target.value;
        setFilterAttribute(value);

        dispatch(updateLookerTabsVisited({ [tabLabel]: false }));
    };
    //#endregion

    //#region Side Effects

    /**
     * Get the URLs for the iFrames
     */
    useEffect(() => {
        if (modelVersionId && customerId && activeTab === tabLabel && !lookerTabLabel) {
            setIsPending(true);
            getModelEmbedUrl({
                lookerPaths: lookerParams.lookerPath.fiveYearProjectionsTab,
                lookerModel: lookerParams.lookerPath.models,
                customerId,
                modelVersionId,
                filters: {
                    attrib_id: filterAttribute,
                    model_table_suffix: filterTableSuffix,
                },
            })
                .then((res) => {
                    if (res.status === 'Success') {
                        setTableUrl(res.data.table);
                        setChartUrl(res.data.chart);
                    }
                })
                .catch(errorHandler)
                .finally(() => {
                    setIsPending(false);
                });
        } else {
            setIsPending(false);
        }
    }, [activeTab, lookerTabLabel, modelVersionId, tabLabel, customerId, filterAttribute, filterTableSuffix]);

    /**
     * Get the Attributes for the dropdowns
     */
    useEffect(() => {
        getAllOutputAttributes().then((res) => {
            /**
             * TODO: fix this so that it will error in a catch rather than a string condition.
             */
            if (res.status === 'Success') {
                setAttributeList(res.data);
            } else {
                errorHandler(res);
            }
        });
    }, []);
    //#endregion

    //#region  Run-time calculations

    //#endregion

    return (
        <div className="tab-box">
            <div className="five-year-projections-filter">
                <Row className="col-10">
                    <VistaToggleButton
                        showChart={showChart}
                        onToggle={(value) => {
                            setShowChart(!value);
                        }}
                    />

                    <span style={{ width: '20px' }} />

                    <Col sm={1} md={1} lg={2} className="col-1">
                        <Form.Control
                            as="select"
                            size="sm"
                            name="filterTableSuffix"
                            id="5yrproj_filterTableSuffix"
                            onChange={handleFilterChange}
                            value={filterTableSuffix}
                        >
                            <option key="addressable" value="addressable">
                                Addressable
                            </option>
                            <option key="global" value="global">
                                Global
                            </option>
                        </Form.Control>
                    </Col>

                    <Col sm={1} md={2} lg={3} className="col-3">
                        <Form.Control
                            as="select"
                            size="sm"
                            name="filterAttribute"
                            id="5yrproj_filterAttribute"
                            onChange={handleAttributeChange}
                            value={filterAttribute}
                            title="Attributes"
                        >
                            {R.compose(
                                R.map((attribute) => (
                                    <option key={attribute.type + attribute.id} value={attribute.id}>
                                        {attribute.label}
                                    </option>
                                )),
                                R.propOr([], filterTableSuffix),
                            )(attributeList)}
                        </Form.Control>
                    </Col>
                </Row>
            </div>

            {/*The rest of this component is practically identical to the OutputTab component except the placement of the */}
            {/*items. Since this is the only one at the moment I don't see a strong need to accommodate the diffs in the */}
            {/*output tab component.*/}
            <Row>
                <Col md={12}>
                    {isPending ? (
                        <Spinner animation="border" role="status" data-testid="spinner" />
                    ) : (
                        <div className="iframe-graph">
                            {chartUrl && (activeTab === tabLabel || lookerTabsVisited.FiveYearProjections) ? (
                                <iframe
                                    className={showChart ? 'd-block' : 'd-none'}
                                    onLoad={loadComplete}
                                    id="looker"
                                    title="looker-graph"
                                    scrolling="no"
                                    src={chartUrl}
                                    data-testid="chart-iframe"
                                />
                            ) : null}

                            {tableUrl && (activeTab === tabLabel || lookerTabsVisited.FiveYearProjections) ? (
                                <iframe
                                    className={showChart ? 'd-none' : 'd-block'}
                                    onLoad={loadComplete}
                                    id="looker"
                                    title="looker-graph"
                                    scrolling="no"
                                    src={tableUrl}
                                    data-testid="table-iframe"
                                />
                            ) : null}
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
}

YearProjectionsTab.propTypes = propTypes;
YearProjectionsTab.defaultProps = defaultProps;

export default YearProjectionsTab;
